import React, { useMemo, useState } from 'react';
import { Chip, Autocomplete, TextField } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import debounce from 'lodash.debounce';

export const SelectAgentAutocomplete = ({ context, selectedApprover, setSelectedApprover, path, filter }) => {
    const getOptions = context.get('get options');
    const [approvers, setApprovers] = useState([]);
    const [isApproverSelected, setIsApproverSelected] = useState(false);
    const [inputApprover, setInputApprover] = useState('');
    const [loading, setLoading] = useState(false);
    const [pasteQuery, setPasteQuery] = useState(false);

    if (!isApproverSelected) {
        setSelectedApprover(null);
    }

    const querySearchValues = (text, pasted) => {
        const [controlFlow, pathInput, filtersInput] = getOptions.getInputs();

        const [successOutput, resultsOutput, errorOutput] = getOptions.getOutputs();

        const getOptionsRunner = context
            .runner(getOptions)
            .set(pathInput, path)
            .set(filtersInput, filter(text))
            .trigger(controlFlow);

        getOptionsRunner
            .waitFor(successOutput)
            .then(() => {
                setApprovers(getOptionsRunner.get(resultsOutput));
                // if pasted and only one exact match is found, apply immediately
                if (pasted) {
                    const results = getOptionsRunner.get(resultsOutput);
                    if (results.length === 1 && results[0].PosIdName === text) {
                        setIsApproverSelected(true);
                        setSelectedApprover(results[0]);
                        setInputApprover('');
                    }
                    setPasteQuery(false);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error('getposition: wait for error rejected with', error);
            });
        getOptionsRunner
            .waitFor(errorOutput)
            .then(() => {
                setPasteQuery(false);
                setLoading(false);
            })
            .catch((error) => {
                console.error('getposition: wait for error rejected with', error);
            });
    };
    // debounce query and keep same callback between react renders (see https://dmitripavlutin.com/react-throttle-debounce/)
    const debouncedQuery = useMemo(() => debounce(querySearchValues, 300), []);

    if (selectedApprover && isApproverSelected === true) {
        return (
            <Chip
                label={`${selectedApprover.PosIdName} (${selectedApprover.PosId})`}
                color="primary"
                icon={<CachedIcon />}
                sx={{ marginBottom: '1vh' }}
                onClick={() => setIsApproverSelected(false)}
            />
        );
    } else if (isApproverSelected === false) {
        return (
            <Autocomplete
                value={selectedApprover}
                onChange={(event, newValue) => {
                    setSelectedApprover(newValue);
                    setIsApproverSelected(true);
                    setInputApprover('');
                }}
                loading={loading}
                loadingText="...Loading"
                inputValue={inputApprover}
                onInputChange={(e, value, reason) => {
                    if (reason === 'reset') {
                        setInputApprover(''); // if a value has been selected, clear the textfield
                    } else {
                        setInputApprover(value);
                    }
                    if (reason === 'input' && value.length > 1) {
                        setLoading(true);
                        debouncedQuery(value, pasteQuery);
                    }
                }}
                onPaste={(event) => {
                    setPasteQuery(true); // query will trigger through onInputChange
                }}
                options={approvers || []}
                disableClearable={true}
                getOptionLabel={(params) => {
                    if (params) {
                        return `${params.PosIdName} (${params.PosId})`;
                    } else return '';
                }}
                size={'small'}
                renderInput={(params) => <TextField {...params} variant="standard" />}
            />
        );
    } else {
        return null;
    }
};
